/**
 * One-line install for dotdotduck.
 *
 * Motivation. The `new DotDotDuck({...})` constructor accepts ~24
 * config knobs. Every field is optional and every subsystem silently
 * degrades if a dependency isn't wired — which is the correct library
 * behaviour, but it means a stranger dropping the package into a page
 * gets a palette (works) + no ducks (silent) + no agent (silent) + no
 * voice (silent) unless they read the docs.
 *
 * `autoInstall()` is the zero-config on-ramp. It:
 *   - Detects the browser locale (falls back to 'en').
 *   - Wires a demo LLM that produces a helpful "connect an LLM"
 *     response for any completion — so `startAgent()` doesn't silently
 *     no-op; the user sees "wire config.llm to enable AI".
 *   - Uses SDK-shipped duck sprites (the tokens.css defaults already
 *     ship these via `dist/duck/`) so the mascot appears without any
 *     `--dddk-*-url` wiring.
 *   - Registers built-in prompts on the process-wide registry so
 *     `dddk.prompts.list()` works immediately.
 *
 * When the host is ready for real integration they pass overrides on
 * top of the defaults:
 *
 *   const dddk = autoInstall({
 *     llm: myOpenAIProvider,          // switch off the demo stub
 *     siteName: 'Acme',
 *     agentName: 'Rex',
 *     paletteCommands: myCommands,
 *     brand: { voice: 'friendly' },
 *   });
 *
 * `autoInstall(overrides)` and `new DotDotDuck({ ...defaults, ...overrides })`
 * are functionally equivalent — the factory just picks the defaults.
 */

import { DotDotDuck } from './orchestrator';
import type { DotDotDuckConfig } from './orchestrator/types';
import { MockLLM } from './skills/evals';
import { registerBuiltinPrompts } from './prompts/defaults';
import type { LLMProvider } from './agent/llm/types';
import { MobileTrigger, type MobileTriggerConfig, type HeroGreetingConfig } from './triggers/mobile';
import { initSpriteDefaults, type SpriteOverrides } from './init-sprite-urls';

/**
 * Options for `autoInstall()`. Every field is optional and passes
 * through to `DotDotDuckConfig` verbatim.
 */
export interface AutoInstallOptions extends Partial<DotDotDuckConfig> {
  /**
   * When true and no `llm` is provided, spin up a demo LLM that
   * returns a canned "wire an LLM" response. Default: `true`. Set to
   * `false` to keep the agent silently no-op (matches raw `new
   * DotDotDuck({})` behaviour) — useful when the host will wire an
   * LLM later at runtime.
   */
  demoLLM?: boolean;

  /**
   * MobileTrigger config. `autoInstall()` auto-attaches a MobileTrigger
   * with `alwaysVisible: true` (desktop shows the FAB too) + the
   * built-in bilingual HERO greeting on first visit. Pass:
   *   - `false` — skip auto-attach entirely (host attaches their own,
   *     or wants no FAB).
   *   - `{}` — accept every default.
   *   - Any subset of `MobileTriggerConfig` — override just the fields
   *     you care about, unspecified fall back to the sensible default.
   *
   * The FAB config specifically defaults to `alwaysVisible: true` here
   * (vs. `new MobileTrigger()` which stays off unless opted in) — the
   * whole point of `autoInstall()` is "you see the mascot without
   * doing anything".
   */
  mobile?: MobileTriggerConfig | false;

  /**
   * Sprite URL overrides. `autoInstall()` computes correct default
   * URLs for the seven bundled duck sprites via `import.meta.url` +
   * sets them on `:root.style`. Pass a per-key override to swap for
   * your brand — the URL you pass wins over the SDK default.
   *
   *   autoInstall({
   *     sprites: {
   *       avatar:    '/my-brand/mascot.png',
   *       swim:      '/my-brand/mascot-side.png',
   *       // brand-mark left as SDK default → keeps the dotdotduck logo
   *       // on the palette footer's "Powered by" strip.
   *     },
   *   });
   */
  sprites?: SpriteOverrides;

  /**
   * HERO greeting override. Convenience alias for
   * `mobile.heroGreeting`. Whichever you pass wins; if you set both,
   * `mobile.heroGreeting` takes precedence.
   *
   *   autoInstall({ heroGreeting: false });                   // disable
   *   autoInstall({ heroGreeting: { title: 'Welcome!' } });   // override title
   */
  heroGreeting?: HeroGreetingConfig | false;
}

/**
 * Zero-config install. Returns a fully-constructed DotDotDuck with
 * sensible defaults. Pass overrides to fill in real host wiring.
 *
 * Idempotent w.r.t. singletons: prompt registry defaults are shared
 * across instances so a second `autoInstall()` call doesn't re-
 * register or duplicate them.
 */
export function autoInstall(overrides: AutoInstallOptions = {}): DotDotDuck {
  // Ensure the built-in prompt registry is populated even if the host
  // never touches a subsystem that would side-effect-register it.
  registerBuiltinPrompts();

  // Compute + inline-set sprite URLs on :root.style so Dwell / cursor
  // / brand-mark work in every setup — bundler or not, subdirectory
  // or root, standard site or embedded WebView.
  initSpriteDefaults(overrides.sprites);

  const { demoLLM: demoLLMFlag, mobile, sprites: _sprites, heroGreeting, ...rest } = overrides;

  const shouldUseDemoLLM = demoLLMFlag !== false && !rest.llm;
  const llm: LLMProvider | undefined = shouldUseDemoLLM
    ? createDemoLLM()
    : (rest.llm as LLMProvider | undefined);

  const config: DotDotDuckConfig = {
    locale: rest.locale ?? detectBrowserLocale(),
    ...rest,
    // Applied AFTER spread so the demo LLM only lands when the host
    // didn't override — spreading `rest.llm: undefined` on top would
    // clobber our fallback otherwise.
    llm: llm as DotDotDuckConfig['llm'],
  };

  const dddk = new DotDotDuck(config);

  // Auto-attach a MobileTrigger unless the host opted out. Default is
  // `alwaysVisible: true` (desktop shows the FAB too) — matches
  // autoInstall's "you see the mascot without doing anything" goal.
  if (mobile !== false) {
    const userMobile: MobileTriggerConfig = mobile ?? {};
    const userFab = userMobile.fab === false ? false : (userMobile.fab ?? {});
    const mergedMobileCfg: MobileTriggerConfig = {
      ...userMobile,
      fab: userFab === false ? false : { alwaysVisible: true, ...userFab },
      // Convenience alias — top-level `heroGreeting` wins if the host
      // passed it directly, but `mobile.heroGreeting` still overrides
      // that (matches "more specific wins").
      heroGreeting: userMobile.heroGreeting ?? heroGreeting ?? {},
    };
    const mt = new MobileTrigger(mergedMobileCfg);
    mt.attachTo(dddk);
  }

  return dddk;
}

/**
 * Detect the browser locale. Currently returns 'zh-TW' for any
 * Chinese variant, else 'en'. Kept intentionally coarse — the SDK
 * ships user-facing i18n for these two, so returning a locale we
 * can't serve just triggers a silent fallback to English.
 *
 * Hosts wanting finer control pass `locale` in the overrides.
 */
function detectBrowserLocale(): string {
  if (typeof navigator === 'undefined') return 'en';
  const raw = navigator.language || 'en';
  if (raw.toLowerCase().startsWith('zh')) return 'zh-TW';
  return 'en';
}

/**
 * Demo LLM factory — emits a friendly setup nudge for every request.
 * Uses MockLLM under the hood so the response contract stays consistent
 * with test-mode setups.
 *
 * The response text is intentionally short and English-only — a demo
 * user will paste it into the palette / agent surface and see it
 * appear verbatim, so it needs to read as "the SDK is alive, plug in
 * an LLM to make it think". Anything longer risks looking like a real
 * agent response.
 */
function createDemoLLM(): MockLLM {
  return new MockLLM({
    responses: () =>
      '[dotdotduck demo mode] Pass a real `llm` to `autoInstall({ llm })` (or `new DotDotDuck({ llm })`) to enable agent + inline-edit + voice cleanup + translate. See https://dddk.perhapxin.com for provider recipes.',
  });
}
